import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ChevronDown, MapPin, Calendar } from "lucide-react";
import { CATEGORIES, DRIVER_AGES } from "@/lib/data";
import CityAutocomplete from "./CityAutocomplete";

/**
 * Search / Get Quote widget — reference layout:
 * Top area (white): labeled "Search / Date / Date" inputs
 * Bottom bar (navy): Car Category | Lead Driver Age | CALL TO RENT + search icon
 */
export default function SearchWidget({ defaultCity = "", defaultCategory = "" }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    pickup_city: defaultCity,
    pickup_date: today,
    dropoff_date: today,
    car_category: defaultCategory,
    lead_driver_age: DRIVER_AGES[2],
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(form).toString();
    navigate(`/contact?${params}`);
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="w-full bg-white border border-line shadow-[0_20px_60px_-24px_rgba(11,37,69,0.30)]"
      data-testid="search-widget"
    >
      {/* Top row — 3 fields side-by-side (2 rows total on mobile) */}
      <div className="grid grid-cols-3 gap-px bg-[#E5E7EB]">
        <TopField label="Search" mobileLabel="City" icon={<MapPin className="h-3.5 w-3.5" strokeWidth={1.8} />}>
          <CityAutocomplete
            value={form.pickup_city}
            onChange={(v) => setForm((f) => ({ ...f, pickup_city: v }))}
            placeholder="Pickup City"
            required
            dataTestId="widget-city"
            inputClassName="widget-top-input"
          />
        </TopField>

        <TopField label="Date" mobileLabel="Pickup" icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.8} />}>
          <input
            type="date" value={form.pickup_date} min={today}
            onChange={handle("pickup_date")} required
            data-testid="widget-pickup-date" className="widget-top-input"
          />
        </TopField>

        <TopField label="Date" mobileLabel="Drop-off" icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.8} />}>
          <input
            type="date" value={form.dropoff_date} min={form.pickup_date}
            onChange={handle("dropoff_date")} required
            data-testid="widget-dropoff-date" className="widget-top-input"
          />
        </TopField>
      </div>

      {/* Bottom bar — navy — 3 columns on mobile too */}
      <div className="bg-[#0B2545] grid grid-cols-3 md:grid-cols-[1fr_1fr_auto] gap-px">
        <label className="relative bg-[#0B2545] px-3 md:px-5 py-3 md:py-3.5 flex items-center gap-2 cursor-pointer group hover:bg-[#0F2C55] transition-colors">
          <select
            value={form.car_category}
            onChange={handle("car_category")}
            data-testid="widget-category"
            className="widget-bottom-input truncate"
          >
            <option value="" className="text-[#0B2545]">Category (optional)</option>
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.name} className="text-[#0B2545]">{c.name}</option>
            ))}
          </select>
          <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4 text-white/70 shrink-0" strokeWidth={1.8} />
        </label>

        <label className="relative bg-[#0B2545] px-3 md:px-5 py-3 md:py-3.5 flex items-center gap-2 cursor-pointer group border-l border-white/10 hover:bg-[#0F2C55] transition-colors">
          <select
            value={form.lead_driver_age}
            onChange={handle("lead_driver_age")}
            data-testid="widget-age"
            className="widget-bottom-input truncate"
          >
            {DRIVER_AGES.map((a) => (
              <option key={a} value={a} className="text-[#0B2545]">Age {a}</option>
            ))}
          </select>
          <ChevronDown className="h-3.5 w-3.5 md:h-4 md:w-4 text-white/70 shrink-0" strokeWidth={1.8} />
        </label>

        <div className="flex border-l border-white/10 md:border-l-0">
          <button
            type="submit"
            className="flex-1 bg-white text-[#0B2545] font-mono text-[10px] md:text-xs tracking-[0.14em] md:tracking-[0.20em] uppercase font-semibold px-2 md:px-8 py-3 md:py-3.5 hover:bg-[#F5F7FA] transition-colors whitespace-nowrap flex items-center justify-center gap-1.5"
            data-testid="widget-submit"
          >
            <span>Inquire</span>
            <Search className="h-3.5 w-3.5 md:hidden" strokeWidth={2} />
          </button>
          <button
            type="submit"
            aria-label="Search"
            className="hidden md:flex bg-[#0F2C55] hover:bg-[#13366B] text-white items-center justify-center px-4 border-l border-white/10 transition-colors"
            data-testid="widget-submit-icon"
          >
            <Search className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>

      <style>{`
        .widget-top-input {
          background: #FFFFFF;
          color: #0B2545;
          width: 100%;
          padding: 0.4rem 0.75rem 0.6rem 0.75rem;
          font-family: 'Inter', sans-serif;
          font-size: 0.78rem;
          font-weight: 500;
          border: 0;
          outline: none;
          appearance: none;
          -webkit-appearance: none;
        }
        @media (min-width: 768px) {
          .widget-top-input { padding: 0.55rem 1.15rem 0.9rem 1.15rem; font-size: 0.92rem; }
        }
        .widget-top-input:focus { background: #F5F7FA; }
        .widget-top-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.55; }
        .widget-top-input::placeholder { color: #94A0AE; font-weight: 400; }
        .widget-top-input.has-chev {
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%230B2545' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
          background-repeat: no-repeat;
          background-position: right 0.7rem center;
          padding-right: 1.6rem;
        }
        @media (min-width: 768px) {
          .widget-top-input.has-chev { background-position: right 1.15rem center; padding-right: 2.5rem; }
        }
        .widget-bottom-input {
          background: transparent;
          color: #FFFFFF;
          width: 100%;
          padding: 0;
          font-family: 'Inter', sans-serif;
          font-size: 0.78rem;
          font-weight: 500;
          border: 0;
          outline: none;
          appearance: none;
          -webkit-appearance: none;
        }
        @media (min-width: 768px) {
          .widget-bottom-input { font-size: 0.92rem; }
        }
        .widget-bottom-input option { background: #FFFFFF; }
      `}</style>
    </motion.form>
  );
}

function TopField({ label, mobileLabel, icon, children }) {
  return (
    <label className="relative flex flex-col bg-white cursor-pointer">
      <span className="px-3 md:px-5 pt-2 md:pt-3.5 pb-0 flex items-center gap-1.5 md:gap-2 text-[9px] md:text-[10px] font-mono tracking-[0.16em] md:tracking-[0.22em] uppercase text-[#5B6472]">
        {icon}
        <span className="md:hidden">{mobileLabel || label}</span>
        <span className="hidden md:inline">{label}</span>
      </span>
      {children}
    </label>
  );
}
